import {
  generateImageMarkup,
  generateVideoMarkup,
  generateExternalVideoMarkup,
  isMobile,
} from '@hh/helper-functions'

class SwiperProductGallery extends HTMLElement {
  constructor() {
    super()

    this.handleVariantChange = this.handleVariantChange.bind(this)
    this.mainProduct = this.closest('main-product')
    if (this.mainProduct) {
      this.mainProduct.registerVariantChangeCallback(this.handleVariantChange)
    }
    
    this.swiperMainDesktop = this.querySelector('.product-gallery__main.desktop')
    this.swiperMainMobile = this.querySelector('.product-gallery__main.mobile')
    this.swiperContainers = [...this.querySelectorAll('swiper-container')]

    this.zoomButtons = [
      ...this.querySelectorAll('.adaptive-image__icon.js-zoom')
    ]

    this.scrollTimer = null
    this.mainProduct = this.closest('main-product')
    // desktop - vars
    this.thumbnailContainer = this.querySelector('.js-thumbnail-container')
    this.thumbnailButtons = [...this.querySelectorAll('.js-thumbnail-button')]
    this.thumbnailListItems = [...this.querySelectorAll('.js-thumbnail')]
    this.isQuickview = this.dataset.isQuickview === 'true'

    this.galleryModal = this.querySelector('.js-gallery-modal')
    this.closeZoomButtons = [...this.querySelectorAll('.js-close-zoom')]
    this.galleryModalImages = this.querySelector('.js-gallery-modal-images')
    this.galleryModalThumbs = this.querySelector('.js-gallery-modal-thumbs')

    this.galleryModalMobile = this.querySelector('.js-gallery-modal-mobile')
    this.closeZoomMobileButtons = [
      ...this.querySelectorAll('.js-close-zoom-mobile'),
    ]

    this._initOptionSelector = this._initOptionSelector.bind(this)

    this.scrollPositions = []
    this.doesPDPHaveStickyGallery =
      [...document.querySelectorAll('.product-gallery--sticky')].length > 0

    this.thumbnailIndex = -1

    this.visibleThumbnails = this.thumbnailListItems.filter(
      (item) => !item.classList.contains('hidden')
    )

    this.variantType = this.getAttribute('galleryType')?.trim()

    this.closeZoomModal = this.closeZoomModal.bind(this)

    this.galleryAdditionalContent =
      this.querySelector('.js-gallery-additional')?.innerHTML || ''

    // mobile bindings not required, simple slider controls nav

    // desktop - bindings
    this.thumbnailButtons.forEach((button) => {
      button.addEventListener('click', this.handleThumbnailClick.bind(this))
      button.addEventListener('keydown', this.handleThumbnailKeydown.bind(this))
    }, false)

    if (this.thumbnailContainer) {

      this.zoomButtons.forEach((button) => {
        button.addEventListener('click', this.handleActiveMediaClick.bind(this))
      })

      this.zoomButtons.forEach((button) => {
        button.addEventListener('keydown', this.handleActiveMediaClick.bind(this))
      })

      this.closeZoomButtons.forEach((button) =>
        button.addEventListener(
          'click',
          this.handleCloseZoomButtonClick.bind(this)
        )
      )
    }

    this.closeZoomMobileButtons.forEach((button) =>
      button.addEventListener(
        'click',
        this.handleCloseZoomButtonClick.bind(this)
      )
    )
  }

  async connectedCallback() {
    await this.mainProduct.isReadyPromise
  }

  get productId() {
    return this.getAttribute('product-id')
  }

  get product() {
    return JSON.parse(
      window.atob(
        document.querySelector(`#main-product-data-${this.productId}`).innerText
      )
    )
  }

  get currentGalleryVariantId() {
    return this.getAttribute('active-variant-id')
  }

  set currentGalleryVariantId(id) {
    this.setAttribute('active-variant-id', id)
  }

  extractSlidesFromHTML(html) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const slides = [...doc.querySelectorAll('swiper-slide')]

    return slides.map((slide) => slide.outerHTML) // Convert to array of HTML elements to HTML strings
  }

  updateSwiper(swiper, slides) {
    swiper.removeAllSlides()
    swiper.addSlide(1, this.extractSlidesFromHTML(slides))
    swiper.update()
    swiper.slideTo(0, 0)
    this.zoomButtons = [
      ...this.querySelectorAll('.adaptive-image__icon.js-zoom')
    ]
    this.zoomButtons.forEach((button) => {
      button.addEventListener('click', this.handleActiveMediaClick.bind(this))
    })
    this.thumbnailButtons = [...this.querySelectorAll('.js-thumbnail-button')]
    this.thumbnailButtons.forEach((button) => {
      button.addEventListener('click', this.handleThumbnailClick.bind(this))
      button.addEventListener('keydown', this.handleThumbnailKeydown.bind(this))
    }, false)
  }

  updateVariantImages(variant) {
    if (!variant) return
    if (String(variant.id) === this.currentGalleryVariantId) return
    const variantImages = this.variantImages[variant.id]

    if (variantImages) {
      if (this.swiperMainDesktop.swiper) {
        this.updateSwiper(this.swiperMainDesktop.swiper, variantImages.gallery)
      }

      if (this.swiperMainMobile.swiper) {
        this.updateSwiper(this.swiperMainMobile.swiper, variantImages.gallery)
      }

      if (this.thumbnailContainer.swiper) {
        this.updateSwiper(this.thumbnailContainer.swiper, variantImages.thumbnails)
      }
    }
    this.currentGalleryVariantId = variant.id
  }

  handleVariantChange(variant) {
    if (!document.startViewTransition) {
      return this.updateVariantImages(variant)
    }

    document.startViewTransition(() => this.updateVariantImages(variant))
  }

  get variantImages() {
    return window[`variantImages${this.product.id}`]
  }

  handleThumbnailClick(event) {
    this.updateSelection({ ...event.target.dataset })
  }

  // Swiper does not automatically handle keyboard events on thumbnails
  handleThumbnailKeydown(e) {
    if (e.code !== 'Space' && e.code !== 'Enter') return;
    const slideIndex = Number.parseInt(e.target.closest('swiper-slide')?.getAttribute('data-swiper-slide-index'));
    if (Number.isNaN(slideIndex)) return;
    this.swiperMainDesktop.swiper.slideTo(slideIndex + 1);
    this.updateSelection({ ...e.target.dataset })
  }

  // selectThumb(element) {
  //   if (element) {
  //     element.querySelector('button').click()
  //   }
  // }

  // selectFirstThumb() {
  //   const currentIndex = parseInt(this.thumbnailContainer?.dataset.scrollIndex)
  //   let firstVisibleThumb = this.visibleThumbnails?.[currentIndex]

  //   setTimeout(function () {
  //     firstVisibleThumb.querySelector('button').click()
  //   }, 350)
  // }

  // clearVisibleThumbs() {
  //   this.thumbnailListItems.map((item) => {
  //     item.classList.remove('slide-visible')
  //     item.querySelector('button').setAttribute('tabindex', -1)
  //   })
  // }

  // tracks which thumbnails are showing
  // selectVisibleThumbs() {
  //   if (this.thumbnailContainer) {
  //     // 1. remove visible class from old thumbs
  //     this.clearVisibleThumbs()
  //     // 2. add visible class to five visible thumbs
  //     let scrollIndex = parseFloat(this.thumbnailContainer.dataset.scrollIndex)

  //     this.visibleThumbnails.map((item) =>
  //       item.setAttribute('aria-hidden', true)
  //     )

  //     this.visibleThumbnails.slice(scrollIndex, scrollIndex + 5).map((item) => {
  //       item.classList.add('slide-visible')
  //       item.setAttribute('aria-hidden', false)
  //       item.querySelector('button').setAttribute('tabindex', 0)
  //     })
  //   }
  // }

  handleActiveMediaClick(event) {

    // on enter key only
    if (event.type === 'keydown' && event.keyCode !== 13) {
      return
    }

    if (document.querySelector('.gift-card-gallery')) {
      return
    }

      document.body.classList.add('frozen')

      if (!this.isQuickview) {
        isMobile()
          ? this.openZoomModalMobile(event.target.parentElement)
          : this.openZoomModal()
      }

      let toggleCloseZoom = this.handleCloseZoomButtonClick.bind(this)

      function handleEsc(e) {
        if (e.keyCode === 27) {
          toggleCloseZoom()
        }
      }

      document.addEventListener('keydown', handleEsc)

  }

  handleCloseZoomButtonClick(event) {
    document.body.classList.remove('frozen')
    this.closeZoomModal()
  }

  updateSelection(data) {
    let thumbnailIndex = parseFloat(data.thumbnailIndex)

    if (thumbnailIndex === -1) {
      console.error('Could not find thumbnail')
      return
    }

    let oldActiveItems = [
      ...this.thumbnailContainer.querySelectorAll('.active'),
    ]

    oldActiveItems.forEach((item) => item.classList.remove('active'))

    if (thumbnailIndex) {
      this.thumbnailIndex = thumbnailIndex
    } else {
      this.thumbnailListItems[0].classList.add('active')

      this.variantThumbnails = this.thumbnailListItems.filter(
        (item) =>
          !item.classList.contains('hidden') &&
          !item.classList.contains('cloned-slide')
      )
      this.thumbnailIndex = -1
      if (this.variantThumbnails.length > 0) {
        this.thumbnailIndex =
          this.variantThumbnails[0].querySelector(
            'button'
          ).dataset.thumbnailIndex
      }
    }
  }

  static get observedAttributes() {
    return ['variant']
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'variant') {
      // On variant change:
      if (!this.variantMap) {
        this._initOptionSelector()
      }

      let refinedAlt =
        this.variantMap &&
        Array.from(this.variantMap).filter(
          (variant) => variant.optionGenName === newValue
        )?.[0]?.name

      if (this.thumbnailContainer) {
        // DESKTOP

        this.thumbnailListItems.forEach((item) => {
          const variantAlt = (item?.dataset?.variantAlt?.trim() || '').replace(/\s-\s.+\s\d+$/i, '')
          if (
            variantAlt !== refinedAlt &&
            variantAlt !== 'All' &&
            variantAlt !== ''
          ) {
            item.classList.add('hidden')
          } else {
            item.classList.remove('hidden')
          }
        })

        let visibleSlidesWithNoClones = this.thumbnailListItems.filter(
          (item) =>
            !item.classList.contains('hidden') &&
            !item.classList.contains('cloned-slide')
        )

        this.thumbnailOffset = visibleSlidesWithNoClones.length

        if (this.thumbnailContainer) {
          this.thumbnailContainer.dataset.scrollIndex = this.thumbnailOffset
        }

        this.visibleThumbnails = this.thumbnailListItems.filter(
          (item) => !item.classList.contains('hidden')
        )
      }
    }
  }

  generateThumbnailMarkup(data, i) {
    return `
      <li>
        <button
          class="js-gallery-thumbnail-button"
          data-index="${i}"
        >
          <img
            alt="${data.alt}"
            src="${data.thumb}"
            loading="eager">
        </button>
      </li>
    `
  }

  setCurrentThumbnail(currentIndex, mobile) {
    if (mobile) {
      this.muteModalMobileScroll = true
    }
    const _currentIndex = Number(currentIndex)
    this.querySelectorAll('.js-gallery-thumbnail-button').forEach((item) =>
      item.classList.remove('active')
    )
    this.querySelectorAll('.js-gallery-thumbnail-button').forEach(
      (item, index) => {
        item.classList[index === _currentIndex ? 'add' : 'remove']('active')
      }
    )

    if (!mobile) {
      this.galleryModal.scrollTo({
        top: this.scrollPositions[_currentIndex],
        behavior: 'smooth',
      })
    } else {
      this.galleryModalImagesMobile.scrollTo({
        left: this.scrollPositions[_currentIndex],
        top: 0,
        behavior: 'smooth',
      })
      this.doMuteModalMobileScroll()
    }
  }

  trapFocus(e) {
    const focusableEls = this.galleryModal.querySelectorAll(
      '.js-close-zoom, .js-gallery-thumbnail-button'
    )
    const firstFocusableEl = focusableEls[0]
    const lastFocusableEl = focusableEls[focusableEls.length - 1]
    const KEYCODE_TAB = 9

    const isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB

    if (!isTabPressed) {
      return
    }

    if (e.shiftKey) {
      /* shift + tab */ if (document.activeElement === firstFocusableEl) {
        lastFocusableEl.focus()
        e.preventDefault()
      }
    } /* tab */ else {
      if (document.activeElement === lastFocusableEl) {
        firstFocusableEl.focus()
        e.preventDefault()
      }
    }
  }

  openZoomModal() {
    this._initModalReady(this.galleryModalImages, this.galleryModalThumbs)
  }

  // This creates a map for easier variant switching
  _initOptionSelector() {
    if (this.variantType === 'none') {
      return
    }

    let selector = document.querySelector('.single-option-selector')
    let options = [...selector.querySelectorAll('option')]

    let liquidVariants = document.querySelector('.hidden-variants')
    let liquidOptions = [...liquidVariants.querySelectorAll('option')]

    let rawVariants = this.product.variants

    let uniqueSelector = options.map((option, index) => {
      let newObject = {
        value: rawVariants[index].id,
        name: liquidOptions[index].innerText,
        optionGenName: option.value,
      }

      return newObject
    })

    this.variantMap = uniqueSelector
  }

  _initModalReady(imagesContainer, thumbsContainer, mobile = false) {
    imagesContainer.innerHTML = ''
    thumbsContainer.innerHTML = ''

    this.classList.remove('product-gallery--sticky')

    this.scrollPositions = []

    let variantThumbs = []
    if (mobile) {
      variantThumbs = Array.from(
        this.querySelectorAll('.js-mobile-slide')
      ).filter((item) => !item.classList.contains('hidden'))
    } else {
      variantThumbs = Array.from(this.querySelectorAll('.js-thumbnail')).filter(
        (item) =>
          item.classList.contains('cloned-slide') !== true &&
          item.classList.contains('hidden') !== true
      )
    }

    const imagesData = []
    let thumbButton = null,
      thumbImg = null
    variantThumbs.forEach((item) => {
      thumbButton = item.querySelector('button')
      thumbImg = item.querySelector('img')
      imagesData.push({
        id: thumbButton.dataset.thumbnailIndex,
        alt: thumbButton.dataset.alt,
        aspectRatio: thumbButton.dataset.aspectRatio ?? null,
        loading: thumbButton.dataset.loading ?? null,
        thumb: thumbImg
          ? thumbImg.dataset.src
          : thumbButton.dataset.thumbSrc ?? null,
        src: thumbButton.dataset.previewImageUrl,
        posterImage: thumbButton.dataset.previewImageUrl,
        video: thumbButton.dataset.video ?? null,
        mediaType: thumbButton.dataset.mediaType,
      })
    })

    let imagesMarkUp = ''
    imagesData.forEach((item) => {
      if (item.video) {
        if (
          item.mediaType === 'external_video' ||
          typeof item.mediaType === 'undefined'
        ) {
          imagesMarkUp += generateExternalVideoMarkup(item)
        } else {
          imagesMarkUp += generateVideoMarkup(item)
        }
      } else {
        imagesMarkUp += generateImageMarkup(item)
      }
    })

    imagesContainer.innerHTML = imagesMarkUp

    let thumbsMarkUp = ''
    imagesData.forEach((item, i) => {
      thumbsMarkUp += this.generateThumbnailMarkup(item, i)
    })
    thumbsContainer.innerHTML = thumbsMarkUp

    if (mobile) {
      this.galleryModalMobile.classList.add('opened')
    } else {
      this.galleryModal.classList.add('m-block')
    }

    let currentIndex = 0
    imagesData.forEach((item, i) => {
      if (Number(item.id) === Number(this.thumbnailIndex)) {
        currentIndex = i
      }
    })

    setTimeout(() => {
      this.scrollPositions = Array.from(imagesContainer.children).reduce(
        (positions, item) => {
          mobile
            ? positions.push(item.offsetLeft)
            : positions.push(item.offsetTop)
          return positions
        },
        []
      )
      this.setCurrentThumbnail(currentIndex, mobile)

      if (!mobile) {
        this.galleryModal.addEventListener('keydown', this.trapFocus.bind(this))
      }
    }, 100)

    this.querySelectorAll('.js-gallery-thumbnail-button').forEach((item) => {
      item.addEventListener(
        'click',
        (e) => {
          this.setCurrentThumbnail(item.dataset.index, mobile)
        },
        false
      )
    })
  }

  closeZoomModal() {
    this.galleryModalImages.innerHTML = ''
    this.galleryModalThumbs.innerHTML = ''
    this.galleryModal.classList.remove('m-block')

    if (this.doesPDPHaveStickyGallery) {
      this.classList.add('product-gallery--sticky')
    }

    this.galleryModal.removeEventListener('keydown', this.trapFocus.bind(this))
  }
}

window.customElements.define('swiper-product-gallery', SwiperProductGallery)